import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Grid,
    IconButton,
    Stack,
    TextField,
    Tooltip,
    Typography
} from '@mui/material';
import { Title, useNotify } from 'react-admin';
import { useTranslation } from 'react-i18next';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import * as Sentry from '@sentry/react';
import { Switch } from '../../components/switch/Switch.tsx';
import { isAdmin } from '../../core/roles.ts';
import DeliveryPriorityService, {
    type CityDeliveryPriority,
    type DeliveryPriorityEntry
} from '../../services/delivery/deliveryPriorityService.ts';

const service = new DeliveryPriorityService();

/** What the server accepts: blank, or a Telegram chat id such as -1001234567890. */
const TELEGRAM_CHAT_ID = /^-?\d{5,20}$/;

/**
 * Where this city's "needs a courier" alerts go. The vello-alerts server reads
 * it; blank sends the city's alerts to that server's default chat.
 */
const TelegramGroupField: React.FC<{
    city: CityDeliveryPriority;
    onStored: (city: CityDeliveryPriority) => void;
}> = ({ city, onStored }) => {
    const { t } = useTranslation();
    const notify = useNotify();
    const stored = city.telegramChatId ?? '';
    const [value, setValue] = useState(stored);
    const [saving, setSaving] = useState(false);

    useEffect(() => setValue(stored), [stored]);

    const trimmed = value.trim();
    const invalid = trimmed !== '' && !TELEGRAM_CHAT_ID.test(trimmed);
    const dirty = trimmed !== stored;

    const onSave = async () => {
        setSaving(true);
        try {
            const updated = await service.setTelegram(city.id, trimmed);
            onStored(updated);
            notify(t('delivery.priority.telegramSaved', { city: updated.name }), { type: 'success' });
        } catch (e: any) {
            Sentry.captureException(e, { tags: { source: 'delivery-priority-telegram' } });
            notify(e?.response?.data?.error ?? t('common.saveFailed', 'Save failed'), { type: 'error' });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'flex-start' }} sx={{ mt: 2 }}>
            <TextField
                id={`telegram-${city.id}`}
                size="small"
                fullWidth
                label={t('delivery.priority.telegramLabel', 'Telegram group for courier alerts')}
                placeholder="-1001234567890"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                error={invalid}
                helperText={invalid
                    ? t('delivery.priority.telegramInvalid', 'Only digits, like -1001234567890.')
                    : t('delivery.priority.telegramHelp', "Add the Vello alerts bot to the city's group and paste the group id here (starts with -100).")}
                inputProps={{ inputMode: 'numeric', style: { fontVariantNumeric: 'tabular-nums' } }}
            />
            <Button
                size="small"
                variant="outlined"
                color="secondary"
                disabled={!dirty || invalid || saving}
                onClick={onSave}
                sx={{ flexShrink: 0, mt: { sm: 0.5 } }}
            >
                {saving ? t('common.saving', 'Saving…') : t('common.save', 'Save')}
            </Button>
        </Stack>
    );
};

const comparable = (entries: DeliveryPriorityEntry[]) =>
    JSON.stringify(entries.map(({ provider, enabled }) => ({ provider, enabled })));

/**
 * One city's list. Our couriers split it: the services above them are asked
 * automatically, top first, each only if the one above declined; the ones
 * below are used only when an admin hands an order over from the board.
 */
const CityCard: React.FC<{ city: CityDeliveryPriority }> = ({ city: initial }) => {
    const { t } = useTranslation();
    const notify = useNotify();
    const [saved, setSaved] = useState<CityDeliveryPriority>(initial);
    const [entries, setEntries] = useState<DeliveryPriorityEntry[]>(initial.entries);
    const [saving, setSaving] = useState(false);
    const [open, setOpen] = useState(initial.isActive);

    const dirty = comparable(entries) !== comparable(saved.entries);
    const ourCouriersAt = entries.findIndex(entry => entry.provider === 'INDEPENDENT');

    const move = (index: number, by: -1 | 1) => {
        const target = index + by;
        if (target < 0 || target >= entries.length) return;
        const next = [...entries];
        [next[index], next[target]] = [next[target], next[index]];
        setEntries(next);
    };

    const toggle = (index: number, enabled: boolean) => {
        setEntries(entries.map((entry, i) => (i === index ? { ...entry, enabled } : entry)));
    };

    const onSave = useCallback(async () => {
        setSaving(true);
        try {
            const stored = await service.save(saved.id, entries.map(({ provider, enabled }) => ({ provider, enabled })));
            setSaved(stored);
            setEntries(stored.entries);
            notify(t('delivery.priority.saved', { city: stored.name }), { type: 'success' });
        } catch (e: any) {
            Sentry.captureException(e, { tags: { source: 'delivery-priority-save' } });
            notify(e?.response?.data?.error ?? t('common.saveFailed', 'Save failed'), { type: 'error' });
        } finally {
            setSaving(false);
        }
    }, [entries, notify, saved.id, t]);

    const wolt = entries.find(entry => entry.provider === 'WOLT');
    const woltPartial = !!wolt?.enabled && wolt.connected
        && saved.woltMappedCount > 0 && saved.woltMappedCount < saved.restaurantCount;
    const woltNowhere = saved.restaurantCount > 0 && saved.woltMappedCount === 0;

    const sectionLabel = (text: string) => (
        <Typography
            variant="caption"
            sx={{ display: 'block', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.04em', mt: 1.5, mb: 0.5 }}
        >
            {text}
        </Typography>
    );

    return (
        <Card variant="outlined" sx={{ height: '100%', opacity: saved.isActive ? 1 : 0.75 }}>
            <CardContent>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
                    <Typography variant="h6" sx={{ mr: 'auto' }}>
                        {saved.name}
                        <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                            {saved.countryCode}
                        </Typography>
                    </Typography>
                    {!saved.isActive && (
                        <Chip size="small" label={t('delivery.priority.beingPrepared', 'Being prepared')} />
                    )}
                    {!saved.customised && (
                        <Chip size="small" variant="outlined" label={t('delivery.priority.default', 'Default')} />
                    )}
                    <IconButton
                        size="small"
                        aria-label={open ? t('delivery.priority.hide', 'Hide') : t('delivery.priority.show', 'Show')}
                        onClick={() => setOpen(!open)}
                    >
                        {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                </Stack>
                <Typography variant="body2" color="text.secondary">
                    {t('delivery.priority.restaurants', { count: saved.restaurantCount })}
                </Typography>

                {open && (
                    <>
                        {woltPartial && (
                            <Alert severity="warning" sx={{ mt: 1.5 }}>
                                {t('delivery.priority.woltPartial', {
                                    mapped: saved.woltMappedCount,
                                    total: saved.restaurantCount
                                })}
                            </Alert>
                        )}

                        {ourCouriersAt > 0 && sectionLabel(t('delivery.priority.automatic', 'Asked automatically, in this order'))}

                        <Stack spacing={0.5}>
                            {entries.map((entry, index) => {
                                const ourCouriers = entry.provider === 'INDEPENDENT';
                                return (
                                    <React.Fragment key={entry.provider}>
                                        {index === ourCouriersAt + 1 && sectionLabel(t('delivery.priority.byHand', 'By hand from the order board'))}
                                        <Stack
                                            direction="row"
                                            alignItems="center"
                                            spacing={1}
                                            sx={{
                                                px: 1,
                                                py: 0.5,
                                                borderRadius: 'var(--radius-6, 6px)',
                                                border: '1px solid',
                                                borderColor: ourCouriers ? 'var(--color-primary)' : 'divider',
                                                bgcolor: ourCouriers ? 'action.hover' : 'transparent',
                                            }}
                                        >
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                sx={{ width: 20, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}
                                            >
                                                {index + 1}
                                            </Typography>
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Typography variant="body2" sx={{ fontWeight: 700 }}>{entry.name}</Typography>
                                                {ourCouriers && (
                                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                                        {t('delivery.priority.ourCouriersHelp', 'The fallback when every service above says no. Always on.')}
                                                    </Typography>
                                                )}
                                                {entry.provider === 'WOLT' && entry.connected && woltNowhere && (
                                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                                        {t('delivery.priority.woltNowhere', 'No restaurant here is set up with Wolt yet, so Wolt is skipped.')}
                                                    </Typography>
                                                )}
                                            </Box>
                                            {!ourCouriers && (
                                                <Chip
                                                    size="small"
                                                    variant="outlined"
                                                    color={entry.connected ? 'success' : 'default'}
                                                    label={entry.connected
                                                        ? t('delivery.priority.connected', 'Connected')
                                                        : t('delivery.priority.notConnected', 'Not connected')}
                                                />
                                            )}
                                            <Tooltip title={ourCouriers
                                                ? t('delivery.priority.alwaysOn', 'Our couriers are always on')
                                                : t('delivery.priority.enabled', 'Use in this city')}>
                                                <span>
                                                    <Switch
                                                        checked={entry.enabled}
                                                        disabled={ourCouriers}
                                                        inputProps={{ 'aria-label': `${entry.name}: ${t('delivery.priority.enabled', 'Use in this city')}` }}
                                                        onChange={(_: any, checked: boolean) => toggle(index, checked)}
                                                    />
                                                </span>
                                            </Tooltip>
                                            <IconButton
                                                size="small"
                                                aria-label={`${t('delivery.priority.moveUp', 'Move up')}: ${entry.name}`}
                                                disabled={index === 0}
                                                onClick={() => move(index, -1)}
                                            >
                                                <ArrowUpwardIcon fontSize="small" />
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                aria-label={`${t('delivery.priority.moveDown', 'Move down')}: ${entry.name}`}
                                                disabled={index === entries.length - 1}
                                                onClick={() => move(index, 1)}
                                            >
                                                <ArrowDownwardIcon fontSize="small" />
                                            </IconButton>
                                        </Stack>
                                    </React.Fragment>
                                );
                            })}
                        </Stack>

                        <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 2 }}>
                            <Button
                                size="small"
                                disabled={!dirty || saving}
                                onClick={() => setEntries(saved.entries)}
                            >
                                {t('delivery.priority.reset', 'Undo changes')}
                            </Button>
                            <Button
                                size="small"
                                variant="contained"
                                color="secondary"
                                disabled={!dirty || saving}
                                onClick={onSave}
                            >
                                {saving ? t('common.saving', 'Saving…') : t('common.save', 'Save')}
                            </Button>
                        </Stack>

                        <TelegramGroupField
                            city={saved}
                            onStored={(stored) => setSaved({ ...saved, telegramChatId: stored.telegramChatId })}
                        />
                    </>
                )}
            </CardContent>
        </Card>
    );
};

/**
 * Per city, which delivery services carry its orders and in what order.
 * Admin only; the backend enforces the same.
 */
const DeliveryPriorityPage = () => {
    const { t } = useTranslation();
    const permitted = isAdmin();
    const [cities, setCities] = useState<CityDeliveryPriority[] | null>(null);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        if (!permitted) return;
        let cancelled = false;
        service.list()
            .then(list => { if (!cancelled) setCities(list); })
            .catch(() => { if (!cancelled) setFailed(true); });
        return () => { cancelled = true; };
    }, [permitted]);

    const title = <Title title={t('resources.deliveryPriority', 'Delivery priority')} />;

    if (!permitted) {
        return (
            <>
                {title}
                <Card sx={{ mt: 2, maxWidth: 640 }}>
                    <CardContent>
                        <Typography variant="body1">
                            {t('settings.notPermitted', 'You do not have permission to view this page.')}
                        </Typography>
                    </CardContent>
                </Card>
            </>
        );
    }

    return (
        <Box sx={{ mt: 2, px: { xs: 2, md: 0 }, pb: 2 }}>
            {title}
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 760, mb: 2 }}>
                {t('delivery.priority.intro')}
            </Typography>

            {failed && (
                <Alert severity="error" sx={{ maxWidth: 760 }}>
                    {t('delivery.priority.loadFailed', 'Could not load the delivery priorities. Reload the page to try again.')}
                </Alert>
            )}

            {!failed && cities === null && <CircularProgress size={24} />}

            {cities && (
                <Grid container spacing={2}>
                    {cities.map(city => (
                        <Grid key={city.id} size={{ xs: 12, md: 6, xl: 4 }}>
                            <CityCard city={city} />
                        </Grid>
                    ))}
                </Grid>
            )}
        </Box>
    );
};

export default DeliveryPriorityPage;
